import { developmentMode, getEnvVar } from './ReactScriptHelper';

import utils = egr.wcf.utils;
import { android, app, ios } from './UserAgent';
import { Dict } from './Types';

let debugMode: boolean = developmentMode;

export function useDebugMode(value: boolean): void {
    debugMode = value;
}

// tslint:disable-next-line:no-any
export function assert(condition: boolean, message?: string, ...data: Array<any>): boolean {
    if (debugMode) {
        // tslint:disable-next-line:no-console
        console.assert(condition, message, ...data);
    }

    return condition;
}

// tslint:disable-next-line:no-any
declare var Influxdb: any;
// tslint:disable-next-line:no-any
let influxdb: any;

interface InfluxTags {
    method: string;
    device: string;
    identifier?: string;
}

const influxdbUrl: string = getEnvVar('INFLUXDB_URL', '');
if (utils.string.isNotNullOrEmpty(influxdbUrl)) {
    influxdb = new Influxdb(influxdbUrl, true);
    setInterval(
        (): void => {
            influxdb.send();
        },
        30000
    );
}

type InfluxDevice = 'ios' | 'android' | 'windows' | 'browser';
function getInfluxDevice(): InfluxDevice {
    if (!app) {
        return 'browser';
    }

    if (android) {
        return 'android';
    }

    if (ios) {
        return 'ios';
    }

    return 'windows';
}

let deviceIdentifier: string | undefined;
export function setDeviceIdentifier(value: string | undefined): boolean {
    if (value == null || /^[a-z0-9_]+$/i.test(value)) {
        deviceIdentifier = value;
        return true;
    }

    return false;
}

export type EndTimerCallback = (additonalMeasurements?: Dict<number>) => void;
export function getDebugTimer(label: string, additonalTags?: Dict): EndTimerCallback {
    const start: number = Date.now();
    return (additonalMeasurements?: Dict<number>): void => {
        const end: number = Date.now();
        if (developmentMode) {
            // tslint:disable-next-line:no-console
            console.log(label + ': ' + (end - start).toString());
        }

        if (influxdb !== undefined) {
            const tags: InfluxTags = {
                method: label.replace(/\s/g, '_'),
                device: getInfluxDevice()
            };

            if (utils.string.isNotNullOrEmpty(deviceIdentifier)) {
                tags.identifier = deviceIdentifier;
            } else {
                tags.identifier = 'unknown';
            }

            influxdb.point(
                'method_execution_time',
                Object.assign(
                    {
                        value: end - start
                    },
                    additonalMeasurements != null ? additonalMeasurements : {}
                ),
                additonalTags != null ? Object.assign(tags, additonalTags) : tags,
                Date.now()
            );
        }
    };
}

export async function withDebugTimer<T = void>(label: string, cb: () => Promise<T>, additonalTags?: Dict): Promise<T> {
    const endTimer: VoidFunction = getDebugTimer(label, additonalTags);
    try {
        const value: T = await cb();
        endTimer();
        return value;
    } catch (e) {
        endTimer();
        throw e;
    }
}

// tslint:disable:no-any
export type PromiseMethodDecorator = (
    target: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(...args: Array<any>) => Promise<any>>
) => TypedPropertyDescriptor<(...args: Array<any>) => Promise<any>> | void;

export function methodWithDebugTimer(label: string): PromiseMethodDecorator {
    return (
        target: object,
        propertyKey: string | symbol,
        descriptor: TypedPropertyDescriptor<(...args: Array<any>) => Promise<any>>
    ) => {
        const method: ((...args: Array<any>) => Promise<any>) | undefined = descriptor.value;
        if (method !== undefined) {
            descriptor.value = function(...args: Array<any>): Promise<any> {
                const self: TypedPropertyDescriptor<(...args: Array<any>) => Promise<void>> = this;
                return withDebugTimer<any>(label, (): Promise<any> => {
                    return method.apply(self, args);
                });
            };
        }
        return descriptor;
    };
}
// tslint:enable:no-any

export function createConsoleOverlay(messageTimeout?: number): void {
    type ConsoleFunc = (value: unknown) => void;
    const div: HTMLDivElement = document.createElement('div');
    div.style.width = '100vw';
    div.style.height = '100vh';
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.zIndex = '99999';
    div.style.pointerEvents = 'none';
    div.style.whiteSpace = 'pre';
    div.style.color = 'green';
    document.body.appendChild(div);

    let clearHandle: number | undefined;
    const buffer: Array<string> = [];
    const updateDiv: VoidFunction = (): void => {
        div.innerText = buffer.join('\r\n');
    };
    const removeLastLine: VoidFunction = (): void => {
        if (buffer.length) {
            buffer.length -= 1;
            updateDiv();
            clearHandle = window.setTimeout(removeLastLine, messageTimeout);
        }
    };

    // tslint:disable-next-line
    const wrapConsoleFunc: (name: string, originFunc: ConsoleFunc) => ConsoleFunc = (name: string, originFunc: ConsoleFunc): ConsoleFunc => {
        return (value: unknown): void => {
            if (messageTimeout != null) {
                clearTimeout(clearHandle);
                clearHandle = window.setTimeout(removeLastLine, messageTimeout);
            }

            buffer.length = 9;

            try {
                buffer.splice(0, 0, JSON.stringify(value, undefined, 4));
            } catch (error) {
                buffer.splice(0, 0, (error as Error).message);
            }

            updateDiv();

            originFunc(value);
        };
    };

    // tslint:disable:no-console
    console.log = wrapConsoleFunc('log', console.log as ConsoleFunc);
    console.warn = wrapConsoleFunc('warn', console.warn as ConsoleFunc);
    console.error = wrapConsoleFunc('error', console.error as ConsoleFunc);
    console.info = wrapConsoleFunc('info', console.info as ConsoleFunc);
    console.clear = ((clearFunction: VoidFunction): VoidFunction => {
        return (): void => {
            buffer.length = 0;
            updateDiv();
            clearFunction();
        };
    })(console.clear);
    // tslint:enable:no-console
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/utils/Debug.ts